import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IPSData } from '../../../../shared/models/ips-data';
import { LatestDepth } from '../../../../shared/models/latest-depth';
import { SidebarService } from '../../../../shared/services/sidebar.service';
import { SensorService } from '../../../../shared/services/sensor.service';
import { subMonths } from 'date-fns';
import { faArrowDown, faArrowUp, faEquals, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { catchError, map, Observable, of } from 'rxjs';
import { ChartData } from '../../../../shared/models/depth-data';

@Component({
  selector: 'app-headband-mobile',
  templateUrl: './headband-mobile.component.html',
  styleUrl: './headband-mobile.component.scss'
})
export class HeadbandMobileComponent {
  @Input() ipsData!: IPSData;
  @Input() hasModel!: boolean;
  @Output() sidebarChange = new EventEmitter<boolean>()
  showSidebar: boolean = false;
  latestDepth!: LatestDepth;
  isPiezo: boolean = false;
  today: Date;
  endDate: string;
  startDate: string;
  showDepthModal: boolean = false;
  faArrowUp = faArrowUp;
  faArrowDown = faArrowDown;
  faEquals = faEquals;
  faQuestion = faQuestion;
  chartData$!: Observable<ChartData>;
  showDepthChart: boolean = false;
  hasDetails: boolean = false;
  showDepthDetail: boolean = false;
  showModeleModal: boolean = false;

  sidebarHeight: string = '70%';
  touchStartY: number = 0;
  touchEndY: number = 0;
  touchStartTime = 0;

  constructor(private sidebarService: SidebarService, private sensorService: SensorService){
    this.today = new Date();
    this.endDate = this.today.toISOString().split('T')[0];
    this.startDate = subMonths(this.today, 1).toISOString().split('T')[0];

    this.sidebarService.sidebarStatus$.subscribe((res)=> {
      this.showSidebar = res;
    });
  }

  ngOnChanges() {
    if (this.ipsData) {
      this.isPiezo = this.ipsData.type_Donnee.type === 'Piezo';
      this.sensorService.getSensorLatestDepth(this.ipsData.identifier, this.ipsData.type_Donnee.type, true).subscribe(
        (res) => {
          this.latestDepth = res;
          if(!res){
            this.latestDepth = {
              date: "2000-01-01T00:00:00",
              value: null,
            }
          }
        },
        (error) => {
          console.error('Error fetching latest depth:', error);
          this.sidebarService.setSidebarStatus(false);
        }
      );

      const params = `startDate=${this.startDate}&endDate=${this.endDate}`;
      this.chartData$ = this.sensorService.getSensorDataDepth(this.ipsData.type_Donnee.type, this.ipsData.identifier, params, true).pipe(
        map(res => {
          this.showDepthChart = res[0].length > 0 && res[0].every((data: { altitudeRepere: undefined; }) => data.altitudeRepere !== undefined);
          this.hasDetails = res[0].length > 0;
          if(this.isPiezo) {
            return {
              typeData: "depth",
              result: res[0]
            };
          } else {
            return {
              typeData: "debit",
              result: res[0]
            };
          }

        }),
        catchError((error) => {
          console.error('Error fetching sensor data depth:', error);
          this.showDepthChart = false;
          return of({ typeData: "depth", result: [] } as ChartData);
        })
      );
    }
  }

  onHide(){
    this.sidebarService.setSidebarStatus(false);
  }

  get headerBackgroundColor() {
    const color = this.ipsData?.couleur || '#FFFFFF';
    return this.hexToRgba(color, 1);
  }

  // get getBackgroundWithOpacity(){
  //   const color = this.ipsData?.couleur || '#FFFFFF';
  //   return this.hexToRgba(color, 0.5);
  // }

  private hexToRgba(hex: string, alpha: number): string {
    let c: any;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + `,${alpha})`;
    }
    throw new Error('Bad Hex');
  }

  get iconClass(): any {
    switch (this.ipsData?.tendance) {
      case 'baisse':
        return faArrowDown;
      case 'hausse':
        return faArrowUp;
      case 'stable':
        return faEquals;
      default:
        return faQuestion;
    }
  }

  get textColor(): string {
    const classToColorMap: { [key: string]: string } = {
      'très haut': 'white',
      'haut': 'white',
      'modérément haut': 'black',
      'moyen': 'black',
      'modérément bas': 'black',
      'bas': 'black',
      'très bas': 'black'
    };
    return classToColorMap[this.ipsData.libelleClasse.toLowerCase()] || 'black';
  }

  // openDepthInfoModal() {
  //   this.showDepthModal = true;
  // }

  // hideDepthInfoModal() {
  //   this.showDepthModal = false;
  // }

  openModeleInfoModal() {
    this.sidebarService.setSidebarDetailStatus(true),
    this.showModeleModal = true;
  }

  hideModeleInfoModal() {
    this.sidebarService.setSidebarDetailStatus(false);
    this.showModeleModal = false;
  }

  showDetails() {
    this.showDepthDetail = !this.showDepthDetail;
  }

  toggleSidebar() {
    this.sidebarHeight = this.sidebarHeight === '70%' ? '100%' : '70%';
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
    this.touchEndY = this.touchStartY;
    this.touchStartTime = Date.now();
  }

  onTouchEnd(event: TouchEvent) {
    const deltaY = this.touchStartY - this.touchEndY;
    const time = Date.now() - this.touchStartTime;

    const isSwipe = Math.abs(deltaY) > 30;
    const isTap = Math.abs(deltaY) < 10 && time < 300; // tap rapide

    if (isSwipe) {
      if (deltaY > 0) {
        this.sidebarHeight = '100%'; // swipe vers le haut
      } else {
        this.sidebarHeight = '70%'; // swipe vers le bas
      }
    } else if (isTap) {
      this.toggleSidebar(); // clic "tap"
    }
  }

}
